import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import * as Promise from 'bluebird';
import { Component } from 'react';
import * as propz from 'propz';
import {
  ColumnDefinition,
  getFilters,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter,
  getPagesCount,
  isFilterExist,
  isSortExist,
  TABLE_SORT_DIRECTION,
} from 'Src/helpers/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Loader } from 'Src/components/Loader/Loader';
import { Grid } from 'Src/components/Grid/Grid';
import { Product } from 'Src/models/products';
import {
  addProduct,
  deleteProduct,
  getAllProducts,
  getProducts,
  getProductsCount,
  updateProduct,
} from 'Src/services/superadmin/products';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { ProductForm } from './ProductForm/ProductForm';
import { getIsDigitalProduct, getIsPreviewAvailable, getOutsidePackageAvailable } from 'Src/helpers/accessor';
import { ResizablePanel } from '../../../../../../components/ResizablePanel/ResizablePanel';
import { GenericSummary } from '../../../../../../components/GenericSummary/GenericSummary';
import { PRODUCT_TABS } from '../../../../../../consts/product';
import { ProductSummary } from './ProductSummary/ProductSummary';
import { getSelectOptionForProductTypes, getSelectOptionForYesOrNo } from '../../../../../../helpers/select';
import { TABS } from '../../../../../../consts/common';
import { convertToFilterWithPlus } from '../../../../../../helpers/table';
import { LIMIT } from '../../../../../../consts/table';
import { ProductType } from '../../../../../../models/productTypes';
import { getAllProductTypes } from '../../../../../../services/superadmin/productTypes';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['name'],
  },
  {
    text: 'Type',
    field: 'productType',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: ['productType', 'name'],
  },
  {
    text: 'Preview',
    field: 'isPreviewAvailable',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: getIsPreviewAvailable,
  },
  {
    text: 'Outside package',
    field: 'isOutsidePackageAvailable',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: getOutsidePackageAvailable,
  },
  {
    text: 'Digital product',
    field: 'isDigitalProduct',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: getIsDigitalProduct,
  },
  {
    text: 'Base price',
    field: 'basePrice',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['pricing', 'price'],
  },
  {
    text: 'Delivery charge',
    field: 'deliveryCharge',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['pricing', 'deliveryCharge'],
  },
];

interface State {
  items: Product[];
  currentPage: number;
  selectedItems: Product[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;
  isProductFormModalOpen: boolean;
  isConfirmationModalOpen: boolean;
  currentTabIndex: number;
  productTypes: ProductType[];
  isErrorModalOpen: boolean;
  errorMessage: string;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Products extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',
      isProductFormModalOpen: false,
      isConfirmationModalOpen: false,
      currentTabIndex: 0,
      productTypes: [],
      isErrorModalOpen: false,
      errorMessage: '',
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true,
    });

    this.setAdditionalItems().then(res => {
      this.setItems();
    });
  }

  setAdditionalItems() {
    const { user } = this.props;

    return getAllProductTypes(user).then(productTypes => {
      this.setState({
        productTypes: productTypes,
      });

      return true;
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true,
      });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters(COLUMNS, search);

    const isShowFilter = isFilterExist(filters);
    const isDataFiltered = isFilterExist(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter(page, where, order);

    const getItemsPromise = getProducts(user, serverQueryFilter);
    const getItemsCountPromise = getProductsCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return Promise.all(promises).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection as TABLE_SORT_DIRECTION,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false,
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const selectedItemsNext = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: selectedItemsNext,
      isSelectAllChecked: true,
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true,
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    getAllProducts(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const selectedItemsNext = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: selectedItemsNext,
        isSelectAllChecked: true,
        isLoading: false,
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue,
            },
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue,
            },
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value,
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue,
        };
      }
    }

    this.setState({
      filters: nextFilters,
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    const filtersConverted = convertToFilterWithPlus(filters);

    if (isFilter) {
      search.push(getSearchFilter(filtersConverted));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: '',
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter,
    });
  };

  onCreateProductClick = () => {
    this.setState({
      isProductFormModalOpen: true,
    });
  };

  onEditProductClick = () => {
    this.setState({
      isProductFormModalOpen: true,
    });
  };

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      currentTabIndex: index,
    });
  };

  onCancelButtonClick = () => {
    this.setState({
      isProductFormModalOpen: false,
    });
  };

  onCloseErrorClick = () => {
    this.setState({
      isErrorModalOpen: false,
      errorMessage: '',
    });
  };

  onSubmitForm = data => {
    const { productTypeId, ...rest } = data;

    const dataToTransfer = {
      ...rest,
      pricing: {
        price: Number(data.pricing.price),
        deliveryCharge: Number(data.pricing.deliveryCharge),
      },
      dimensions: {
        weight: Number(data.dimensions.weight),
        length: Number(data.dimensions.length),
        width: Number(data.dimensions.width),
        height: Number(data.dimensions.height),
      },
      productType: { typeId: productTypeId },
    };

    if (dataToTransfer.pic.key === '' && dataToTransfer.pic.ticketId === '') {
      dataToTransfer.pic = {};
    }

    delete dataToTransfer.pic.ticketId;

    const { selectedItems, total } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true,
    });

    if (selectedItems.length === 1) {
      const productUpdated = selectedItems[0];
      const { id } = productUpdated;

      updateProduct(user, id, dataToTransfer).then(res => {
        this.setState({
          isProductFormModalOpen: false,
          selectedItems: [res],
          isSelectAllChecked: false,
        });
        this.setItems();
      });
    } else {
      addProduct(user, dataToTransfer).then(res => {
        const lastPage = getPagesCount(total + 1, LIMIT);
        this.setState({
          isProductFormModalOpen: false,
          selectedItems: [res],
          isSelectAllChecked: false,
        });
        this.setCurrentPageParams(lastPage);
        this.setItems();
      });
    }
  };

  onDeleteProductClick = () => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true,
      isConfirmationModalOpen: false,
    });

    Promise.all(selectedItems.map(selectedItem => deleteProduct(user, selectedItem.id)))
      .then(res => {
        this.setState({
          selectedItems: [],
          isSelectAllChecked: false,
        });

        this.setItems();
      })
      .catch(error => {
        const errorTextInObject = propz.get(error, ['response', 'data', 'details', 'text', 'errmsg'], '');
        const errorText =
          typeof error.response.data.details.text === 'string' ? error.response.data.details.text : errorTextInObject;
        console.error(errorText);
        this.setState({
          isLoading: false,
          isConfirmationModalOpen: false,
          isErrorModalOpen: true,
          errorMessage: errorText,
        });
      });
  };

  showConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: true,
    });
  };

  hideConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: false,
    });
  };

  renderSendingError(): React.ReactNode {
    const { errorMessage, isErrorModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isErrorModalOpen}
        title={'Error'}
        body={errorMessage}
        buttonCancelText={'Ok'}
        onCloseClick={this.onCloseErrorClick}
      />
    );
  }

  renderConfirmationModal(): React.ReactNode {
    const { isConfirmationModalOpen, selectedItems } = this.state;

    return (
      <SimpleModal
        isOpen={isConfirmationModalOpen}
        title={'Confirmation'}
        body={`Are you sure you want to remove ${selectedItems.length === 1 ? 'product' : 'products'}?`}
        buttonText={'Remove'}
        buttonCancelText={'Cancel'}
        onButtonClick={this.onDeleteProductClick}
        onCloseClick={this.hideConfirmationModal}
      />
    );
  }

  renderFormModal(): React.ReactNode {
    const { isProductFormModalOpen, selectedItems, productTypes } = this.state;
    const { user } = this.props;

    if (selectedItems.length === 1) {
      const product = selectedItems[0];
      return (
        <SimpleModal isOpen={isProductFormModalOpen} customClass={'modal-lg'}>
          <ProductForm
            product={product}
            onCancel={this.onCancelButtonClick}
            onSubmit={this.onSubmitForm}
            productTypes={productTypes}
            user={user}
          />
        </SimpleModal>
      );
    } else {
      return (
        <SimpleModal isOpen={isProductFormModalOpen} customClass={'modal-lg'}>
          <ProductForm
            onCancel={this.onCancelButtonClick}
            onSubmit={this.onSubmitForm}
            productTypes={productTypes}
            user={user}
          />
        </SimpleModal>
      );
    }
  }

  renderGrid(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isLoading,
      isSelectAllChecked,
      currentPage,
      total,
      productTypes,
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const actionItems = [
      {
        itemText: 'Create product',
        onItemClick: this.onCreateProductClick,
        isActive: selectedItems.length === 0,
      },
      {
        itemText: 'Edit product',
        onItemClick: this.onEditProductClick,
        isActive: selectedItems.length === 1,
      },
      {
        itemText: selectedItems.length === 1 ? 'Delete product' : 'Delete products',
        onItemClick: this.showConfirmationModal,
        isActive: selectedItems.length > 0,
      },
    ];

    const filterOptions = {
      productType: getSelectOptionForProductTypes(productTypes),
      isPreviewAvailable: getSelectOptionForYesOrNo(),
      isOutsidePackageAvailable: getSelectOptionForYesOrNo(),
      isDigitalProduct: getSelectOptionForYesOrNo(),
    };
    return (
      <Grid
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
      />
    );
  }

  getTabs(): React.ReactNode {
    const { selectedItems, currentTabIndex } = this.state;
    const { user } = this.props;

    const tabs = PRODUCT_TABS;
    const currentTab = tabs[currentTabIndex];
    const [product] = selectedItems;

    switch (currentTab) {
      case TABS.SUMMARY:
        return <ProductSummary productItem={product} user={user} />;
      default:
        return <div />;
    }
  }

  renderSummary(): React.ReactNode {
    const { selectedItems, currentTabIndex } = this.state;
    const isOneItemSelected = selectedItems.length === 1;

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select a product above</div>;
    }

    const tabs = this.getTabs();

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={PRODUCT_TABS} currentTabIndex={currentTabIndex}>
        {tabs}
      </GenericSummary>
    );
  }

  render() {
    const { isLoading, isProductFormModalOpen, isConfirmationModalOpen, isErrorModalOpen } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isProductFormModalOpen || isConfirmationModalOpen || isErrorModalOpen ? 'mt-3 modal-open' : 'mt-3';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderFormModal()}
        {this.renderConfirmationModal()}
        {this.renderSendingError()}
        <div className="row">
          <div className="col-md-12">
            <ResizablePanel>
              {this.renderGrid()}
              {this.renderSummary()}
            </ResizablePanel>
          </div>
        </div>
      </div>
    );
  }
}
